/**
 * Eigene Listen (F-33) - rein und getestet: Vorlagen, Mengen erkennen,
 * Supermarkt-Abteilungen vorschlagen, Reihenfolge, Vorschlaege beim Tippen
 * und die Auswertung je Liste (Leitlinie 8).
 */
import type { HaeufigerEintrag, Liste, ListenEintrag, ListenVorlage } from '@/domain/listen/typen';

export interface VorlagenInfo {
  label: string;
  name: string;
  symbol: string;
  beschreibung: string;
  gruppen: string[];
  mitMenge?: boolean;
  mitBewertung?: boolean;
}

export const ABTEILUNGEN = [
  'Obst & Gemüse', 'Brot & Backwaren', 'Kühlregal', 'Fleisch & Fisch', 'Tiefkühl',
  'Vorrat', 'Süßes & Snacks', 'Getränke', 'Drogerie', 'Haushalt',
];

export const VORLAGEN: Record<ListenVorlage, VorlagenInfo> = {
  einkauf: {
    label: 'Einkauf', name: 'Einkaufsliste', symbol: 'einkauf', mitMenge: true, gruppen: ABTEILUNGEN,
    beschreibung: 'Mit Mengen („2× Milch“) und nach Supermarkt-Abteilungen sortiert.',
  },
  ideen: {
    label: 'Ideen zum Ausprobieren', name: 'Ideen zum Ausprobieren', symbol: 'idee', mitBewertung: true, gruppen: [],
    beschreibung: 'Nach dem Ausprobieren mit 1–10 bewerten.',
  },
  pack: {
    label: 'Packliste', name: 'Packliste', symbol: 'koffer', gruppen: ['Kleidung', 'Hygiene', 'Technik', 'Dokumente', 'Sonstiges'],
    beschreibung: 'Zum Wiederverwenden: „Alle zurücksetzen“ vor der nächsten Reise.',
  },
  leer: { label: 'Leer', name: '', symbol: 'liste', gruppen: [], beschreibung: 'Einfache Liste ohne Extras.' },
};

const EINHEIT = '(?:x|×|stk\\.?|stück|g|kg|ml|l|liter|pck\\.?|packung(?:en)?|dosen?|flaschen?|bund|becher|gläser|glas)';
const VORNE = new RegExp(`^(\\d+(?:[.,]\\d+)?\\s*${EINHEIT}?)\\s+(.+)$`, 'i');
const HINTEN = new RegExp(`^(.+?)\\s+(\\d+(?:[.,]\\d+)?\\s*${EINHEIT})$`, 'i');

/** „2× Milch“, „500 g Mehl“, „Eier 10 Stück“ -> Menge und Text. */
export function mengeTrennen(eingabe: string): { text: string; menge?: string } {
  const t = eingabe.trim().replace(/\s+/g, ' ');
  const v = VORNE.exec(t);
  if (v) return { text: v[2]!.trim(), menge: v[1]!.replace(/\s+/g, ' ').trim() };
  const h = HINTEN.exec(t);
  if (h) return { text: h[1]!.trim(), menge: h[2]!.replace(/\s+/g, ' ').trim() };
  return { text: t };
}

/** Haeufige Waren je Abteilung - Stichwort am Wortanfang reicht („Vollmilch“ findet „milch“ nicht, „Milchreis“ schon). */
const WAREN: Record<string, string[]> = {
  'Obst & Gemüse': ['apfel', 'äpfel', 'banane', 'birne', 'orange', 'zitrone', 'tomate', 'gurke', 'salat', 'paprika', 'zwiebel', 'knoblauch', 'kartoffel', 'karotte', 'möhre', 'brokkoli', 'zucchini', 'pilze', 'champignon', 'beeren', 'erdbeer', 'trauben', 'avocado', 'spinat', 'kräuter', 'ingwer'],
  'Brot & Backwaren': ['brot', 'brötchen', 'toast', 'baguette', 'croissant', 'knäckebrot', 'semmel'],
  'Kühlregal': ['milch', 'joghurt', 'quark', 'käse', 'butter', 'sahne', 'eier', 'ei', 'frischkäse', 'mozzarella', 'skyr', 'tofu', 'aufschnitt', 'schmand'],
  'Fleisch & Fisch': ['hähnchen', 'huhn', 'pute', 'hack', 'rind', 'schwein', 'wurst', 'schinken', 'lachs', 'thunfisch', 'fisch', 'steak'],
  'Tiefkühl': ['tk', 'tiefkühl', 'pizza', 'eis', 'pommes'],
  'Vorrat': ['nudeln', 'pasta', 'reis', 'mehl', 'zucker', 'salz', 'öl', 'essig', 'haferflocken', 'müsli', 'linsen', 'bohnen', 'konserve', 'tomatensoße', 'passata', 'honig', 'marmelade', 'kaffee', 'tee', 'gewürz', 'pfeffer', 'brühe'],
  'Süßes & Snacks': ['schokolade', 'chips', 'kekse', 'nüsse', 'gummibärchen', 'riegel'],
  'Getränke': ['wasser', 'saft', 'cola', 'bier', 'wein', 'limo', 'sprudel'],
  'Drogerie': ['zahnpasta', 'shampoo', 'duschgel', 'deo', 'seife', 'creme', 'rasier', 'taschentücher', 'pflaster', 'zahnbürste'],
  'Haushalt': ['spülmittel', 'waschmittel', 'müllbeutel', 'küchenrolle', 'klopapier', 'toilettenpapier', 'schwamm', 'alufolie', 'backpapier', 'batterien'],
};

/** Gruppe fuer einen neuen Eintrag: zuerst aus frueheren Eintraegen, dann aus den haeufigen Waren - nur, wenn die Liste die Gruppe hat. */
export function gruppeFuer(text: string, liste: Pick<Liste, 'gruppen' | 'haeufig'>): string | undefined {
  const t = text.trim().toLowerCase();
  if (!t || !liste.gruppen.length) return undefined;
  const frueher = liste.haeufig?.find((h) => h.text.toLowerCase() === t)?.gruppe;
  if (frueher && liste.gruppen.includes(frueher)) return frueher;
  const woerter = t.split(/[\s-]+/);
  for (const [gruppe, waren] of Object.entries(WAREN)) {
    if (!liste.gruppen.includes(gruppe)) continue;
    if (woerter.some((w) => waren.some((x) => w === x || (x.length > 3 && w.startsWith(x))))) return gruppe;
  }
  return undefined;
}

/** Reihenfolge zwischen zwei Nachbarn - nur der verschobene Eintrag aendert sich. */
export function orderZwischen(vorher: number | undefined, nachher: number | undefined): number {
  if (vorher === undefined && nachher === undefined) return 1;
  if (vorher === undefined) return nachher! - 1;
  if (nachher === undefined) return vorher + 1;
  return (vorher + nachher) / 2;
}

export interface Abschnitt {
  /** `null` = ohne Gruppe; `erledigt` = der Abschnitt der abgehakten Eintraege. */
  gruppe: string | null;
  erledigt?: true;
  eintraege: ListenEintrag[];
}

/**
 * Offene Eintraege nach Gruppen (in der Reihenfolge der Liste, ohne Gruppe
 * zuerst), darin nach `order`; Abgehakte gesammelt am Ende, zuletzt
 * abgehakte oben. Leere Gruppen sind dabei - ob sie erscheinen, entscheidet die Anzeige.
 */
export function abschnitte(eintraege: ListenEintrag[], gruppen: string[]): Abschnitt[] {
  const offen = eintraege.filter((e) => !e.erledigt).sort((a, b) => a.order - b.order);
  const bekannt = new Set(gruppen);
  const ohne = offen.filter((e) => !e.gruppe || !bekannt.has(e.gruppe));
  const liste: Abschnitt[] = [];
  if (ohne.length || !gruppen.length) liste.push({ gruppe: null, eintraege: ohne });
  for (const g of gruppen) liste.push({ gruppe: g, eintraege: offen.filter((e) => e.gruppe === g) });
  const erledigt = eintraege.filter((e) => e.erledigt)
    .sort((a, b) => (b.erledigtAm ?? '').localeCompare(a.erledigtAm ?? '') || a.order - b.order);
  if (erledigt.length) liste.push({ gruppe: null, erledigt: true, eintraege: erledigt });
  return liste;
}

const MAX_HAEUFIG = 300;

/** Einen genutzten Eintrag fuer spaetere Vorschlaege merken. */
export function haeufigMerken(haeufig: HaeufigerEintrag[] = [], e: Pick<ListenEintrag, 'text' | 'gruppe' | 'menge'>): HaeufigerEintrag[] {
  const key = e.text.trim().toLowerCase();
  if (!key) return haeufig;
  const alt = haeufig.find((h) => h.text.toLowerCase() === key);
  const neu: HaeufigerEintrag = { text: e.text.trim(), anzahl: (alt?.anzahl ?? 0) + 1 };
  const gruppe = e.gruppe ?? alt?.gruppe;
  if (gruppe) neu.gruppe = gruppe;
  const menge = e.menge ?? alt?.menge;
  if (menge) neu.menge = menge;
  return [neu, ...haeufig.filter((h) => h !== alt)].sort((a, b) => b.anzahl - a.anzahl).slice(0, MAX_HAEUFIG);
}

/** Vorschlaege beim Tippen: frueher Genutztes, das gerade nicht offen auf der Liste steht. */
export function vorschlaege(haeufig: HaeufigerEintrag[] = [], eingabe: string, offen: ListenEintrag[], max = 6): HaeufigerEintrag[] {
  const q = mengeTrennen(eingabe).text.toLowerCase();
  if (q.length < 1) return [];
  const da = new Set(offen.map((e) => e.text.toLowerCase()));
  const passt = haeufig.filter((h) => !da.has(h.text.toLowerCase()) && h.text.toLowerCase().includes(q) && h.text.toLowerCase() !== q);
  return passt.sort((a, b) => Number(b.text.toLowerCase().startsWith(q)) - Number(a.text.toLowerCase().startsWith(q)) || b.anzahl - a.anzahl).slice(0, max);
}

export interface ListenStatistik {
  offen: number;
  erledigt: number;
  /** 0-1 */
  anteil: number;
  bewertet: number;
  /** Durchschnitt der Bewertungen, eine Nachkommastelle. */
  schnitt: number | null;
  beste: ListenEintrag[];
}

export function statistik(eintraege: ListenEintrag[]): ListenStatistik {
  const erledigt = eintraege.filter((e) => e.erledigt).length;
  const bewertet = eintraege.filter((e) => typeof e.bewertung === 'number');
  const summe = bewertet.reduce((s, e) => s + e.bewertung!, 0);
  return {
    offen: eintraege.length - erledigt,
    erledigt,
    anteil: eintraege.length ? erledigt / eintraege.length : 0,
    bewertet: bewertet.length,
    schnitt: bewertet.length ? Math.round((summe / bewertet.length) * 10) / 10 : null,
    beste: [...bewertet].sort((a, b) => b.bewertung! - a.bewertung!).slice(0, 3),
  };
}

/** Ein Link braucht ein Schema - „example.org“ wird zu „https://example.org“. */
export function linkNormal(eingabe: string): string | undefined {
  const t = eingabe.trim();
  if (!t) return undefined;
  if (/^https?:\/\//i.test(t)) return t;
  if (/^[\w-]+(\.[\w-]+)+(\/.*)?$/.test(t)) return `https://${t}`;
  return undefined;
}
